import React, { useState, useEffect } from 'react';
import { Handshake, TrendingUp, Users, Award, Youtube, Instagram, Facebook, Linkedin } from 'lucide-react';
import axios from 'axios';

const Franchise = () => {
  const [videos, setVideos] = useState([]);

  // Helper function to convert YouTube URL to embed format
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    
    let videoId = null;
    if (url.includes('youtube.com/watch?v=')) {
      videoId = url.split('v=')[1]?.split('&')[0];
    } else if (url.includes('youtube.com/embed/')) {
      // Already in embed format
      return url;
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    }
    
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}`;
    }
    return url;
  };

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL || '';
        const response = await axios.get(`${API}/api/videos`);
        // Filter for influencer videos only
        const influencerVideos = response.data.filter(video => video.type === 'influencer' && video.isActive);
        if (influencerVideos.length > 0) {
          // Map to ensure videoUrl field is set
          setVideos(influencerVideos.map(video => ({
            id: video._id,
            title: video.title,
            videoUrl: video.url
          })));
        }
      } catch (error) {
        console.error('Error fetching videos:', error);
      }
    };
    fetchVideos();
  }, []);

  // Show placeholder frames if no videos
  const displayVideos = videos.length > 0 ? videos : [
    { id: 'placeholder-1', title: 'Add Your Video', videoUrl: '' },
    { id: 'placeholder-2', title: 'Add Your Video', videoUrl: '' },
    { id: 'placeholder-3', title: 'Add Your Video', videoUrl: '' },
    { id: 'placeholder-4', title: 'Add Your Video', videoUrl: '' }
  ];

  return (
    <section className="py-20 bg-black relative overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1533577116850-9af94dacc2b6?w=1920&q=80"
          alt="Partnership"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="inline-block bg-gradient-to-r from-pink-500 to-purple-500 text-white px-6 py-2 rounded-full text-sm font-bold">
              INFLUENCERS FAVOURITE
            </div>
            
            <h2 className="text-5xl md:text-6xl font-black text-white leading-tight">
              INFLUENCERS <span className="text-yellow-400">LOVE US.</span>
            </h2>
            
            <p className="text-xl text-gray-300 leading-relaxed font-light">
              We are In talks and Videos of Influencers Across India.
            </p>

            {/* Social Media Icons */}
            <div className="flex items-center space-x-4 pt-4">
              <a 
                href="https://www.snapchat.com/add/indya.bungy?share_id=R69eXHq-EQw&locale=en-IN" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full flex items-center justify-center transition-all transform hover:scale-110"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_jump-fix/artifacts/iuh9o4eb_S1.png"
                  alt="Snapchat"
                  className="w-10 h-10 object-contain"
                />
              </a>
              <a 
                href="https://www.instagram.com/indyabungy/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full flex items-center justify-center transition-all transform hover:scale-110"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_jump-fix/artifacts/te2glmk7_S2.png"
                  alt="Instagram"
                  className="w-10 h-10 object-contain"
                />
              </a>
              <a 
                href="https://www.linkedin.com/in/siddharth-pareek-872070153/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full flex items-center justify-center transition-all transform hover:scale-110"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_jump-fix/artifacts/8etv5x0q_S3.png"
                  alt="LinkedIn"
                  className="w-10 h-10 object-contain"
                />
              </a>
              <a 
                href="https://www.facebook.com/people/Indya-Bungy/61569196414294/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full flex items-center justify-center transition-all transform hover:scale-110"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_jump-fix/artifacts/55il97x6_S4.png"
                  alt="Facebook"
                  className="w-10 h-10 object-contain"
                />
              </a>
              <a 
                href="https://www.youtube.com/@INDYABUNGY" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full flex items-center justify-center transition-all transform hover:scale-110"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_jump-fix/artifacts/g5ng96xm_S5.png"
                  alt="YouTube"
                  className="w-10 h-10 object-contain"
                />
              </a>
            </div>
          </div>

          {/* Right Side - Video Showcase */}
          {/* Mobile: 1 big + 3 small, Desktop: 2x2 grid */}
          <div className="md:hidden space-y-4">
            {/* First Video - Large on Mobile */}
            {displayVideos[0] && (
              <div className="bg-gradient-to-br from-yellow-400/10 to-yellow-400/5 rounded-2xl border border-yellow-400/20 hover:border-yellow-400/40 transition-all duration-300 overflow-hidden group">
                <div className="aspect-video relative">
                  {displayVideos[0].videoUrl ? (
                    <iframe
                      src={getYouTubeEmbedUrl(displayVideos[0].videoUrl)}
                      title={displayVideos[0].title}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  ) : (
                    <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                      <Youtube className="text-gray-600" size={48} />
                    </div>
                  )}
                </div>
                {displayVideos[0].title && (
                  <div className="p-4">
                    <h3 className="text-white font-bold text-sm line-clamp-2">{displayVideos[0].title}</h3>
                  </div>
                )}
              </div>
            )}

            {/* Remaining 3 Videos - small on mobile */}
            <div className="grid grid-cols-3 gap-3">
              {displayVideos.slice(1).map((video) => (
                <div
                  key={video.id}
                  className="bg-gradient-to-br from-yellow-400/10 to-yellow-400/5 rounded-2xl border border-yellow-400/20 hover:border-yellow-400/40 transition-all duration-300 overflow-hidden group"
                >
                  <div className="aspect-video relative">
                    {video.videoUrl ? (
                      <iframe
                        src={getYouTubeEmbedUrl(video.videoUrl)}
                        title={video.title}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    ) : (
                      <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                        <Youtube className="text-gray-600" size={24} />
                      </div>
                    )}
                  </div>
                  {video.title && (
                    <div className="p-2">
                      <h3 className="text-white font-bold text-xs line-clamp-2">{video.title}</h3>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Desktop: 2x2 grid */}
          <div className="hidden md:grid grid-cols-2 gap-6">
            {displayVideos.map((video) => (
              <div
                key={video.id}
                className="bg-gradient-to-br from-yellow-400/10 to-yellow-400/5 rounded-2xl border border-yellow-400/20 hover:border-yellow-400/40 transition-all duration-300 overflow-hidden group"
              >
                <div className="aspect-video relative">
                  {video.videoUrl ? (
                    <iframe
                      src={getYouTubeEmbedUrl(video.videoUrl)}
                      title={video.title}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  ) : (
                    <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                      <Youtube className="text-gray-600" size={48} />
                    </div>
                  )}
                </div>
                {video.title && (
                  <div className="p-4">
                    <h3 className="text-white font-bold text-sm line-clamp-2">{video.title}</h3>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Franchise;